import type { HourlyWeatherSample, LatLng } from '../types';

export function coordCacheKey(coord: LatLng): string {
  return `${coord.latitude.toFixed(3)},${coord.longitude.toFixed(3)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export const WMO_LABELS: Record<number, string> = {
  0: 'Clear',
  1: 'Mainly Clear',
  2: 'Partly Cloudy',
  3: 'Overcast',
  45: 'Fog',
  48: 'Depositing Rime Fog',
  51: 'Light Drizzle',
  53: 'Moderate Drizzle',
  55: 'Dense Drizzle',
  56: 'Light Freezing Drizzle',
  57: 'Dense Freezing Drizzle',
  61: 'Slight Rain',
  63: 'Moderate Rain',
  65: 'Heavy Rain',
  66: 'Light Freezing Rain',
  67: 'Heavy Freezing Rain',
  71: 'Slight Snow',
  73: 'Moderate Snow',
  75: 'Heavy Snow',
  77: 'Snow Grains',
  80: 'Slight Rain Showers',
  81: 'Moderate Rain Showers',
  82: 'Violent Rain Showers',
  85: 'Slight Snow Showers',
  86: 'Heavy Snow Showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with Hail',
  99: 'Severe Thunderstorm with Hail',
};

export function getWeatherLabel(code: number | null | undefined): string {
  if (code == null) return '—';
  return WMO_LABELS[code] || `Code ${code}`;
}

export function getWeatherCategory(code: number | null | undefined): 'sun' | 'cloud' | 'rain' | 'snow' | 'thunder' | 'fog' {
  if (code == null) return 'sun';
  if (code === 0 || code === 1) return 'sun';
  if (code === 2 || code === 3) return 'cloud';
  if ([45, 48].includes(code)) return 'fog';
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return 'rain';
  if ([71, 73, 75, 77, 85, 86].includes(code)) return 'snow';
  if ([95, 96, 99].includes(code)) return 'thunder';
  return 'cloud';
}

/** In-memory cache for hourly weather samples per coord key. */
const weatherCache = new Map<string, HourlyWeatherSample[]>();

type RawOpenMeteoHourly = {
  time?: number[];
  temperature_2m?: (number | null)[];
  precipitation_probability?: (number | null)[];
  precipitation?: (number | null)[];
  weather_code?: (number | null)[];
  wind_speed_10m?: (number | null)[];
  is_day?: (number | null)[];
  apparent_temperature?: (number | null)[];
  visibility?: (number | null)[];
  wind_gusts_10m?: (number | null)[];
  uv_index?: (number | null)[];
};

type RawOpenMeteoResponse = {
  timezone?: string;
  hourly?: RawOpenMeteoHourly;
};

async function fetchWithRetry(url: string): Promise<any> {
  let attempt = 0;
  const max = 5;

  while (attempt < max) {
    const res = await fetch(url);
    if (res.ok) return await res.json();
    if (res.status === 429) {
      const ra = res.headers.get('Retry-After');
      const sec = ra ? parseFloat(ra.trim()) : NaN;
      const pauseMs = Number.isFinite(sec) && sec > 0 ? Math.min(60_000, sec * 1000) : 2 ** attempt * 500;
      await sleep(pauseMs);
      attempt++;
      continue;
    }
    throw new Error(`Weather HTTP ${res.status}`);
  }
  throw new Error('Weather API rate limited.');
}

function parseHourlyResponse(
  raw: RawOpenMeteoResponse,
): HourlyWeatherSample[] {
  const h = raw.hourly;
  if (!h || !Array.isArray(h.time) || h.time.length === 0) return [];

  const tz = raw.timezone || null;
  const times = h.time;
  const temps = h.temperature_2m || [];
  const pops = h.precipitation_probability || [];
  const precip = h.precipitation || [];
  const codes = h.weather_code || [];
  const winds = h.wind_speed_10m || [];
  const isDayFlags = h.is_day || [];
  const apparent = h.apparent_temperature || [];
  const vis = h.visibility || [];
  const gusts = h.wind_gusts_10m || [];
  const uv = h.uv_index || [];

  const out: HourlyWeatherSample[] = [];
  for (let i = 0; i < times.length; i++) {
    const epochSec = times[i]!;
    out.push({
      time: new Date(epochSec * 1000),
      epochSec,
      temperatureC: temps[i] ?? null,
      precipProbability: pops[i] ?? null,
      precipitationMm: precip[i] ?? null,
      weatherCode: codes[i] ?? null,
      windKmh: winds[i] ?? null,
      isDay: (isDayFlags[i] ?? 1) === 1,
      apparentTemperatureC: apparent[i] ?? null,
      visibility: vis[i] ?? null,
      windGustsKmh: gusts[i] ?? null,
      uvIndex: uv[i] ?? null,
      timezone: tz,
    });
  }
  return out;
}

/**
 * Fetch hourly weather forecasts for multiple coordinates in batch.
 * Checks cache first and only fetches missing coordinates.
 */
export async function fetchBatchHourlyForecast(
  coordinates: LatLng[],
  forecastDays: number = 16,
): Promise<Map<string, HourlyWeatherSample[]>> {
  const resultMap = new Map<string, HourlyWeatherSample[]>();
  const missingCoords: LatLng[] = [];
  const missingKeys: string[] = [];

  for (const coord of coordinates) {
    const key = coordCacheKey(coord);
    if (weatherCache.has(key)) {
      resultMap.set(key, weatherCache.get(key)!);
    } else if (!missingKeys.includes(key)) {
      missingKeys.push(key);
      missingCoords.push(coord);
    }
  }

  if (missingCoords.length === 0) {
    return resultMap;
  }

  // Batch in chunks of up to 50 coordinates to respect URL length
  const CHUNK_SIZE = 50;
  for (let c = 0; c < missingCoords.length; c += CHUNK_SIZE) {
    const chunk = missingCoords.slice(c, c + CHUNK_SIZE);
    const lats = chunk.map((pt) => pt.latitude.toFixed(4)).join(',');
    const lons = chunk.map((pt) => pt.longitude.toFixed(4)).join(',');

    const url = new URL('https://api.open-meteo.com/v1/forecast');
    url.searchParams.set('latitude', lats);
    url.searchParams.set('longitude', lons);
    url.searchParams.set('windspeed_unit', 'kmh');
    url.searchParams.set('timezone', 'auto');
    url.searchParams.set('timeformat', 'unixtime');
    url.searchParams.set('forecast_days', String(forecastDays));
    url.searchParams.set(
      'hourly',
      'temperature_2m,precipitation_probability,precipitation,weather_code,wind_speed_10m,is_day,apparent_temperature,visibility,wind_gusts_10m,uv_index',
    );

    const json = await fetchWithRetry(url.toString());
    const responses: RawOpenMeteoResponse[] = Array.isArray(json) ? json : [json];

    for (let i = 0; i < chunk.length; i++) {
      const coord = chunk[i]!;
      const key = coordCacheKey(coord);
      const rawResp = responses[i];
      if (rawResp) {
        const parsed = parseHourlyResponse(rawResp);
        weatherCache.set(key, parsed);
        resultMap.set(key, parsed);
      }
    }
  }

  return resultMap;
}

/**
 * Find nearest hourly sample to target date/time instant within tolerance.
 * Returns undefined if instant is outside the forecast horizon.
 */
export function nearestHourly(
  samples: HourlyWeatherSample[],
  target: Date,
  maxDeltaMs: number = 3 * 3600 * 1000,
): HourlyWeatherSample | undefined {
  if (!samples.length) return undefined;
  const targetMs = target.getTime();
  let best = samples[0]!;
  let bestDelta = Math.abs(best.time.getTime() - targetMs);

  for (let i = 1; i < samples.length; i++) {
    const s = samples[i]!;
    const delta = Math.abs(s.time.getTime() - targetMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = s;
    }
  }

  if (bestDelta > maxDeltaMs) {
    return undefined;
  }

  return best;
}
